import { Container } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import styles from "./privacy-policy.module.scss";

const PrivacyPolicy = () => {
  return (
    <div className={styles.privacyPage}>
      <Container>
        <div className={styles.header}>
          <Link to="/" className={styles.backLink}>
            ← Back to Home
          </Link>
          <h1 className={styles.title}>Privacy & Cookie Policy</h1>
          <p className={styles.lastUpdated}>Last Updated: November 3, 2025</p>
        </div>

        <div className={styles.content}>
          <section className={styles.section}>
            <h2>1. Introduction</h2>
            <p>
              BioLean ("we," "our," or "us") is committed to protecting your privacy.
              This Privacy & Cookie Policy explains how we collect, use, and safeguard
              your information when you visit our website at biolean.dk (the "Website").
            </p>
            <p>
              By using our Website, you agree to the collection and use of information
              in accordance with this policy.
            </p>
          </section>

          <section className={styles.section}>
            <h2>2. Information We Collect</h2>

            <h3>2.1 Analytics Data</h3>
            <p>
              We use Google Analytics to understand how visitors interact with our Website.
              This service collects:
            </p>
            <ul>
              <li>IP addresses (anonymized)</li>
              <li>Browser type and version</li>
              <li>Device information (desktop, mobile, tablet)</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website</li>
              <li>Geographic location (country/city level only)</li>
            </ul>

            <h3>2.2 Contact Form Data</h3>
            <p>
              When you submit our contact form, we collect:
            </p>
            <ul>
              <li>Your name</li>
              <li>Email address</li>
              <li>Subject and message content</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li>
                <strong>Analytics:</strong> To understand website traffic patterns,
                improve user experience, and optimize our content
              </li>
              <li>
                <strong>Communication:</strong> To respond to your inquiries submitted
                through the contact form
              </li>
              <li>
                <strong>Service Improvement:</strong> To enhance our Website's
                functionality and content based on user behavior
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>4. Cookies</h2>

            <h3>4.1 What Are Cookies?</h3>
            <p>
              Cookies are small text files stored on your device when you visit a website.
              They help websites remember your preferences and provide analytics data.
            </p>

            <h3>4.2 Cookies We Use</h3>
            <p>We use the following types of cookies:</p>

            <div className={styles.cookieTable}>
              <table>
                <thead>
                  <tr>
                    <th>Cookie Name</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>biolean_cookie_consent</td>
                    <td>Stores your cookie consent preference</td>
                    <td>365 days</td>
                  </tr>
                  <tr>
                    <td>_ga</td>
                    <td>Google Analytics - distinguishes users</td>
                    <td>2 years</td>
                  </tr>
                  <tr>
                    <td>_ga_*</td>
                    <td>Google Analytics - maintains session state</td>
                    <td>2 years</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>4.3 Managing Cookies</h3>
            <p>You can manage or delete cookies through:</p>
            <ul>
              <li>
                <strong>Cookie Consent Banner:</strong> Click "Decline" when you first
                visit our Website
              </li>
              <li>
                <strong>Browser Settings:</strong> Most browsers allow you to refuse
                cookies or delete existing ones through their settings
              </li>
              <li>
                <strong>Google Analytics Opt-out:</strong> Install the{" "}
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Analytics Opt-out Browser Add-on
                </a>
              </li>
            </ul>
            <p>
              Note: Disabling cookies may affect your experience on our Website,
              though all core functionality will remain available.
            </p>
          </section>

          <section className={styles.section}>
            <h2>5. Third-Party Services</h2>

            <h3>5.1 Google Analytics</h3>
            <p>
              We use Google Analytics, a web analytics service provided by Google LLC.
              Google Analytics uses cookies to help analyze how users interact with our Website.
            </p>
            <p>
              The information generated by cookies about your use of the Website
              (including your IP address) will be transmitted to and stored by Google
              on servers in the United States.
            </p>
            <p>
              Learn more about Google's privacy practices:{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Privacy Policy
              </a>
            </p>

            <h3>5.2 Azure Communication Services</h3>
            <p>
              Contact form submissions are processed through Microsoft Azure Communication
              Services for email delivery. Your contact information is transmitted securely
              and is not stored by Microsoft beyond the time needed for delivery.
            </p>
          </section>

          <section className={styles.section}>
            <h2>6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect
              your personal information against unauthorized access, alteration, disclosure,
              or destruction. This includes:
            </p>
            <ul>
              <li>HTTPS encryption for all data transmission</li>
              <li>Secure email delivery through Microsoft Azure</li>
              <li>reCAPTCHA protection to prevent spam and abuse</li>
              <li>Regular security updates and monitoring</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>7. Data Retention</h2>
            <ul>
              <li>
                <strong>Analytics Data:</strong> Retained by Google Analytics for 26 months,
                then automatically deleted
              </li>
              <li>
                <strong>Contact Form Data:</strong> Email submissions are retained as long
                as necessary to respond to your inquiry and for our business records
              </li>
              <li>
                <strong>Cookies:</strong> Expire according to the durations listed in
                Section 4.2
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>8. Your Rights (GDPR/CCPA)</h2>
            <p>
              If you are located in the European Economic Area (EEA) or California,
              you have certain rights regarding your personal information:
            </p>
            <ul>
              <li>
                <strong>Right to Access:</strong> Request a copy of the personal
                information we hold about you
              </li>
              <li>
                <strong>Right to Rectification:</strong> Request correction of
                inaccurate information
              </li>
              <li>
                <strong>Right to Erasure:</strong> Request deletion of your
                personal information
              </li>
              <li>
                <strong>Right to Object:</strong> Object to our processing of
                your personal information
              </li>
              <li>
                <strong>Right to Data Portability:</strong> Request transfer of
                your information to another service
              </li>
              <li>
                <strong>Right to Withdraw Consent:</strong> Withdraw your consent
                to cookie usage at any time
              </li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{" "}
              <a href="mailto:info@biolean.dk">info@biolean.dk</a>
            </p>
          </section>

          <section className={styles.section}>
            <h2>9. Children's Privacy</h2>
            <p>
              Our Website is not intended for children under 16 years of age.
              We do not knowingly collect personal information from children.
              If you are a parent or guardian and believe your child has provided
              us with personal information, please contact us.
            </p>
          </section>

          <section className={styles.section}>
            <h2>10. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries
              other than your country of residence, including the United States
              (Google Analytics servers). These countries may have different data
              protection laws than your jurisdiction.
            </p>
          </section>

          <section className={styles.section}>
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy & Cookie Policy from time to time.
              We will notify you of any changes by posting the new policy on this page
              and updating the "Last Updated" date at the top.
            </p>
            <p>
              We encourage you to review this policy periodically for any changes.
              Changes to this policy are effective when posted on this page.
            </p>
          </section>

          <section className={styles.section}>
            <h2>12. Contact Us</h2>
            <p>
              If you have any questions about this Privacy & Cookie Policy, please contact us:
            </p>
            <ul className={styles.contactInfo}>
              <li>
                <strong>Email:</strong>{" "}
                <a href="mailto:info@biolean.dk">info@biolean.dk</a>
              </li>
              <li>
                <strong>Website:</strong>{" "}
                <a href="https://biolean.dk" target="_blank" rel="noopener noreferrer">
                  biolean.dk
                </a>
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>13. Legal Basis for Processing (GDPR)</h2>
            <p>
              For users in the EEA, our legal basis for collecting and using
              personal information depends on the information concerned and the
              context in which we collect it:
            </p>
            <ul>
              <li>
                <strong>Consent:</strong> You have given us explicit consent to
                use cookies for analytics (via the cookie consent banner)
              </li>
              <li>
                <strong>Legitimate Interest:</strong> Processing is necessary for
                our legitimate interests in operating and improving our Website,
                provided such interests are not overridden by your rights
              </li>
              <li>
                <strong>Contractual Necessity:</strong> Processing your contact form
                data is necessary to respond to your inquiry
              </li>
            </ul>
          </section>
        </div>

        <div className={styles.footer}>
          <Link to="/" className={styles.backLink}>
            ← Back to Home
          </Link>
        </div>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;
